import { ArcwareConfig, ArcwareConfigParams, DefaultUrl } from "./ArcwareConfig";
import { ArcwarePixelStreaming } from "./ArcwarePixelStreaming";
import { ArcwareApplication } from "./ArcwareApplication";
import { WebsocketState } from "./domain/ConnectionIdentifier";

type DeepPartial<T> = T extends Function
  ? T
  : T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

let globalPixelStreaming: ArcwarePixelStreaming | null = null;
let globalConfig: ArcwareConfig | null = null;
let globalApplication: ArcwareApplication | null = null;
let previousShareId: string | null = null;
let previousProjectId: string | null = null;

export function ArcwareInit(
  { shareId, projectId }: { shareId?: string; projectId?: string },
  configuration?: DeepPartial<ArcwareConfigParams>
) {
  if (shareId && !shareId.startsWith("share-")) throw new Error(`Unexpected shareId-format: '${shareId}'.`);

  if (shareId && previousShareId !== shareId) {
    globalConfig = null;
    globalPixelStreaming = null;
    globalApplication = null;
    previousShareId = shareId;
  }
  if (projectId && previousProjectId !== projectId) {
    globalConfig = null;
    globalPixelStreaming = null;
    globalApplication = null;
    previousProjectId = projectId;
  }

  if (globalPixelStreaming && globalConfig && globalApplication) {
    console.warn("ArcwareInit: Returning existing instance to prevent multiple WebSocket connections.");

    return {
      Config: globalConfig,
      PixelStreaming: globalPixelStreaming,
      Application: globalApplication,
    };
  }

  const Config = new ArcwareConfig({
    useUrlParams: configuration?.useUrlParams === true,
    initialSettings: {
      ss: DefaultUrl,
      AutoConnect: false,
      StartVideoMuted: true,
      AutoPlayVideo: true,
      KeyboardInput: true,
      MouseInput: true,
      GamepadInput: true,
      TouchInput: true,
      XRControllerInput: true,
      UseMic: true,
      SuppressBrowserKeys: true,
      FakeMouseWithTouches: false,
      ForceMonoAudio: false,
      HoveringMouse: true,
      ...configuration?.initialSettings,
    },
    settings: {
      shareId,
      projectId,
      fullscreenButton: true,
      audioButton: true,
      stopButton: false,
      infoButton: false,
      micButton: false,
      settingsButton: false,
      connectionStrengthIcon: false,
      connectionIdentifierLoggingDisabled: undefined,
      errorHandler: undefined,
      loveLetterHandler: undefined,
      loveLetterLogging: undefined,
      queueHandler: undefined,
      sessionIdHandler: undefined,
      ...configuration?.settings,
    },
  });

  const PixelStreaming = new ArcwarePixelStreaming(Config);
  const Application = new ArcwareApplication({ stream: PixelStreaming });

  // Store globally to prevent multiple instances
  globalConfig = Config;
  globalPixelStreaming = PixelStreaming;
  globalApplication = Application;

  return {
    Config,
    PixelStreaming,
    Application,
  };
}